import {getRecaptchaToken} from "../interaction/recaptcha";
import {showToast} from './toast';
import {hideModal, loadAndShowModal} from "./modal";
import {getModalIdByPath} from '../utils/RoutesToModalId';
import {
  changePassword,
  confirmRegister,
  login,
  register,
  resendVerification,
  resetPassword, resetPasswordChange
} from "../api/authentication";
import {trans} from "../i18n/i18n";
import {hideSpinner, showSpinner} from "./spinner";
import {Toast} from "../consts/toast";
import {route} from "ziggy-js";
import {getRegisterValidationRules, initRegisterFormValidation} from "./registerFormValidator";
import {validateForm, rerenderValidationNew} from "../utils/validation";
import {getLoginValidationRules, getLoginWhitelist, initLoginFormValidation} from "./loginFormValidator";
import {getWhitelistRegister} from "./registerFormValidator";
import {
  getResetPasswordValidationRules,
  getWhitelistResetPassword,
  initResetPasswordFormValidation
} from "./resetPasswordValidator";
import {initChangePasswordFormValidation} from "./changePasswordValidator";
import {trackEvent} from "../tracking/gtm";

document.addEventListener('DOMContentLoaded', async () => {

  const currentPath = window.location.pathname;
  const initialModalId = getModalIdByPath(currentPath);

  if (initialModalId) {
    await handleModalTriggerClick(initialModalId);
    history.replaceState({}, '', route('home'));
  }

  bindModalTriggerLinks();

  if (document.body.dataset.emailVerificationRequired === 'true') {
    await loadAndShowModal('confirmEmail', {
      onLoad: () => {
        initializeConfirmRegisterModal();
        bindModalTriggerLinks();
      }
    });
  }

  if (document.body.dataset.showChangePasswordModal === 'true') {
    await loadAndShowModal('change-password', {
      onLoad: () => {
        initializePasswordChangeModal(false);
        bindModalTriggerLinks();
      }
    });
  }
});

/**
 * Handles the click event for modal triggers.
 * Prevents the default action and loads the appropriate modal based on the 'data-modal-id' attribute.
 *
 * @returns {Promise<void>}
 * @param modalId
 */
async function handleModalTriggerClick(modalId, linkParams = {}) {
  try {
    const modalInitFunctions = {
      'login': initializeLoginModal,
      'register': initializeRegisterModal,
      'reset-password': initializeResetPasswordModal,
      'change-password': initializePasswordChangeModal,
    };

    // Get redirect_to parameter from URL or link params
    const urlParams = new URLSearchParams(window.location.search);
    const redirectTo = linkParams.redirect_to || urlParams.get('redirect_to');

    // Load the modal with a specific onLoad initialization function if needed
    const onLoad = modalInitFunctions[modalId] || null;
    const queryParams = {};
    if (redirectTo) {
      queryParams.redirect_to = redirectTo;
    }

    await loadAndShowModal(modalId, {onLoad, queryParams});
  } catch (error) {
    console.error('Error loading modal content:', error);
  }
}

/**
 * Initializes the login modal by binding all relevant events.
 *
 * @returns {void}
 */
export async function initializeLoginModal() {
  bindAction('#google-login-button', bindGoogleOAuth);
  bindLoginFormSubmission();
  initLoginFormValidation();

  trackEvent("tries_login");
}

/**
 * Initializes the registration modal by binding relevant events.
 *
 * @returns {void}
 */
function initializeRegisterModal() {
  bindAction('#google-register-button', bindGoogleOAuth);
  bindRegisterFormSubmission();
  initRegisterFormValidation();

  trackEvent("tries_sign_up");
}

/**
 * Initializes the reset password modal by binding relevant events.
 *
 * @returns {void}
 */
function initializeResetPasswordModal() {
  bindResetPasswordFormSubmission();
  initResetPasswordFormValidation();

  trackEvent("tries_reset_password");
}

/**
 * Initializes the confirm registration modal by binding relevant events.
 *
 * @returns {void}
 */
export function initializeConfirmRegisterModal({redirectAfterConfirm} = {}) {
  bindConfirmRegisterFormSubmission(redirectAfterConfirm);
  bindResendVerificationFormSubmission();

  trackEvent("tries_confirm_email");
}

function initializePasswordChangeModal(isUserLogged) {
  bindPasswordChangeSubmission(isUserLogged);
  //initializeModalTriggers();
  initChangePasswordFormValidation();

  trackEvent("tries_change_password");
}

/**
 * Binds the submission event for the login form.
 * Handles form submission, performs reCAPTCHA validation, sends an asynchronous request,
 * and displays feedback based on the response.
 *
 * @returns {void}
 */
function bindLoginFormSubmission() {
  const loginForm = document.querySelector('#login-form');
  if (!loginForm) return;

  loginForm.addEventListener('submit', async (event) => {
    event.preventDefault();

    const whitelist = getLoginWhitelist();
    const hasErrors = validateForm(loginForm, getLoginValidationRules(), whitelist);

    if (hasErrors) {
      hideSpinner('loginSpinner');
      return;
    }

    try {
      showSpinner('loginSpinner');

      const recaptchaAction = loginForm.getAttribute('data-recaptcha-action');
      const token = await getRecaptchaToken(recaptchaAction);

      const formData = new FormData(loginForm);
      formData.append('recaptcha_token', token);

            const data = await login(formData);
            if (data.redirect_url) {
                window.location.href = data.redirect_url;
            } else {
                window.location.reload();
            }
        } catch (error) {
            displayFormErrors(error, trans("Authentication failed. Please check your email and password."));
        } finally {
            hideSpinner('loginSpinner');
        }
    });
}

/**
 * Binds the submission event for the registration form.
 * Handles form submission, performs reCAPTCHA validation, sends an asynchronous request,
 * and displays feedback based on the response.
 *
 * @returns {void}
 */
function bindRegisterFormSubmission() {
  const registerForm = document.querySelector('#register-form');
  if (!registerForm) return;

  registerForm.addEventListener('submit', async (event) => {
    event.preventDefault();

    const whitelist = getWhitelistRegister();
    const hasErrors = validateForm(registerForm, getRegisterValidationRules(), whitelist);

    if (hasErrors) {
      hideSpinner('registerSpinner');
      return;
    }

    try {
      showSpinner('registerSpinner');

      const recaptchaAction = registerForm.getAttribute('data-recaptcha-action');
      const token = await getRecaptchaToken(recaptchaAction);

      const formData = new FormData(registerForm);
      formData.append('recaptcha_token', token);

            await register(formData);

            const redirectTo = formData.get("redirect_to");
            await loadAndShowModal('confirmEmail', {
                queryParams: { email: formData.get("email") || "" },
                onLoad: () => {
                    initializeConfirmRegisterModal({ 
                        redirectAfterConfirm: redirectTo 
                    });
                    bindModalTriggerLinks();
                }
            });
            showToast(trans('Registration successful. Please verify your email.'), Toast.SUCCESS);
        } catch (error) {
            displayFormErrors(error, trans('Registration failed. Please try again later or contact us if the problem persists.'));
        } finally {
            hideSpinner('registerSpinner');
        }
    });
}

/**
 * Binds the submission event for the reset password form.
 * Handles form submission, sends an asynchronous request to reset the password,
 * and provides feedback to the user based on the response.
 *
 * @returns {void}
 */
function bindResetPasswordFormSubmission() {
  const resetPasswordForm = document.querySelector('#reset-password-form');
  if (!resetPasswordForm) return;

  resetPasswordForm.addEventListener('submit', async (event) => {
    event.preventDefault();

    const whitelist = getWhitelistResetPassword();
    const hasErrors = validateForm(resetPasswordForm, getResetPasswordValidationRules(), whitelist);

    if (hasErrors) {
      hideSpinner('resetPasswordSpinner');
      return;
    }

        try {
            showSpinner('resetPasswordSpinner');
            const formData = new FormData(resetPasswordForm);
            const data = await resetPassword(formData);
            trackEvent("reset_password_sent", {email: formData.get("email")});
            hideModal();
            resetPasswordForm.reset();
            showToast(data.message || trans('Password reset link has been sent to the email (if it’s registered with us).'), Toast.SUCCESS);
        } catch (error) {
          displayFormErrors(error, trans('Failed to send email. Please try again.'));
        } finally {
            hideSpinner('resetPasswordSpinner');
        }
    });
}

/**
 * Binds the submission event for the confirm registration form.
 * Handles form submission, sends an asynchronous request to confirm email,
 * and provides feedback based on the response.
 *
 * @returns {void}
 */
function bindConfirmRegisterFormSubmission(redirectAfterConfirm) {
  const confirmRegisterForm = document.querySelector('#register-confirm-form');
  if (!confirmRegisterForm) return;

  confirmRegisterForm.addEventListener('submit', async (event) => {
    event.preventDefault();

    const submitButton = document.querySelector('#verify-account-button');
    let userEmail = '';

    try {
      showSpinner('confirmRegisterSpinner');

      // Disable submit button for 3 seconds
      if (submitButton) {
        submitButton.disabled = true;
      }

      const formData = new FormData(confirmRegisterForm);
      await confirmRegister(formData);
      userEmail = formData.get("email");

      trackEvent("email_confirmed", {email: userEmail});      
      hideModal();
      showToast(trans('Email verified successfully!'), Toast.SUCCESS);
      
      if (redirectAfterConfirm) {
        window.location.href = redirectAfterConfirm;
            } else {
                window.location.reload();
            }
        } catch (error) {
            // Re-enable submit button after 3 seconds to prevent spamming
            if (submitButton) {
                setTimeout(() => {
                    submitButton.disabled = false;
                }, 3000);
            }

            displayFormErrors(error, trans("Failed to verify email. Please try again."));
        } finally {
            hideSpinner('confirmRegisterSpinner');
        }
    });
}

/**
 * Binds the submission event for the resend verification form.
 * Handles the form submission, sends an asynchronous request to resend the verification email,
 * and displays appropriate feedback based on the response.
 *
 * If the form is successfully submitted, a success toast is shown. Otherwise, error messages are displayed.
 * The function also shows a loading spinner during the request and hides it afterward.
 *
 * @returns {void}
 */
function bindResendVerificationFormSubmission() {
  const resendVerificationForm = document.querySelector('#resend-verification-form');
  if (!resendVerificationForm) return;

  resendVerificationForm.addEventListener('submit', async (event) => {
    event.preventDefault();

        try {
            showSpinner('confirmRegisterSpinner');
            const formData = new FormData(resendVerificationForm);
            await resendVerification(formData);
            trackEvent("resend_email_confirmation", {email: formData.get("email")});

            showToast(trans('Verification email has been sent.'), Toast.SUCCESS);
            resetFormErrors();
        } catch (error) {
            displayFormErrors(error, trans('Failed to resend verification email. Please try again.'));
        } finally {
            hideSpinner('confirmRegisterSpinner');
        }
    });
}

/**
 * Binds the submission event for the change password form.
 * Handles the form submission, sends an asynchronous request to change the password,
 * and displays appropriate feedback based on the response.
 *
 * If the form is successfully submitted, a success toast is shown and the form is reset.
 * Otherwise, error messages are displayed. The function also shows a loading spinner during
 * the request and hides it afterward.
 *
 * @returns {void}
 */
function bindPasswordChangeSubmission(isUserLogged = true) {
  const changePasswordForm = document.querySelector('#change-password-form');
  if (!changePasswordForm) return;

  const tokenInput = changePasswordForm.querySelector('input[name="token"]');
  const emailInput = changePasswordForm.querySelector('input[name="email"]');

  if (!tokenInput.value) {
    tokenInput.value = document.body.dataset.token || '';
  }

  if (!emailInput.value) {
    emailInput.value = document.body.dataset.email || '';
  }

  changePasswordForm.addEventListener('submit', async (event) => {
    event.preventDefault();

        try {
            showSpinner('changePasswordSpinner');
            const formData = new FormData(changePasswordForm);
            isUserLogged ? await changePassword(formData) : await resetPasswordChange(formData);
            trackEvent("password_changed", {email: formData.get("email")});
            
            showToast(trans('Your password has been changed!'), Toast.SUCCESS);
            hideModal();

            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } catch (error) {
            displayFormErrors(error, trans('Failed to change password. Please try again.'));
        } finally {
            hideSpinner('changePasswordSpinner');
        }
    });
}


/**
 * Binds an action to a specific selector.
 *
 * @param {string} selector - The selector for the element(s) to bind the action to.
 * @param {function} action - The function to execute for the action.
 * @returns {void}
 */
function bindAction(selector, action) {
  const elements = document.querySelectorAll(selector);
  elements.forEach(element => action(element));
}

/**
 * Binds Google OAuth actions to login and register buttons.
 *
 * @param {HTMLElement} element - The element to bind the action to.
 * @returns {void}
 */
function bindGoogleOAuth(element) {
  const googleLoginUrl = element.getAttribute('data-url');
  element.addEventListener('click', () => {
    trackEvent("tries_google_login");

    window.location.href = googleLoginUrl;
  });
}

function resetFormErrors() {
    const authErrorDiv = document.getElementsByClassName('auth-errors')[0];
    if (!authErrorDiv) {
        console.error("Missing .auth-errors element");
        return;
    }

    authErrorDiv.innerHTML = '';
}

/**
 * Displays form errors in the specified div with the class 'auth-errors'.
 * Clears any existing errors before appending the new error messages.
 *
 * @param {Error} error - The error object containing the response status and data.
 * @param {string} fallbackMessage - The fallback message to display if the response does not contain errors.
 */
function displayFormErrors(error, fallbackMessage = null) {
    const errors = error?.response?.data?.errors ?? (fallbackMessage ? [fallbackMessage] : []);
    if (!errors || (Array.isArray(errors) && errors.length === 0)) {
        return;
    }

    const authErrorDiv = document.getElementsByClassName('auth-errors')[0];
    if (authErrorDiv) {
        authErrorDiv.innerHTML = '';
    }

    // Field-specific errors object: { fieldName: [messages] }
    if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
        Object.entries(errors).forEach(([field, messages]) => {
            const input = document.querySelector(`[name="${field}"]`) || document.getElementById(field);
            if (input) {
                const msgs = Array.isArray(messages) ? messages : [messages];
                rerenderValidationNew(input, msgs);
            } else if (authErrorDiv) {
                // Fallback to top error box if input not found
                const msgs = Array.isArray(messages) ? messages : [messages];
                msgs.forEach((msg) => {
                    const el = document.createElement('span');
                    el.classList.add('text-danger', 'text-center');
                    el.textContent = msg;
                    authErrorDiv.appendChild(el);
                    authErrorDiv.appendChild(document.createElement('br'));
                });
            }
        });
        return;
    }

    // Array of generic messages -> show in top error box
    if (Array.isArray(errors) && authErrorDiv) {
        errors.forEach(message => {
            const errorElement = document.createElement('span');
            errorElement.classList.add('text-danger', 'text-center');
            errorElement.textContent = message;
            authErrorDiv.appendChild(errorElement);
            authErrorDiv.appendChild(document.createElement('br'));
        });
        return;
    }

    console.error('Unexpected format for errors:', errors);
}

export function bindModalTriggerLinks() {
  document.removeEventListener('click', modalClickHandler);
  document.addEventListener('click', modalClickHandler);
}

async function modalClickHandler(e) {
  const target = e.target.closest('a');

  if (!target || target.target === '_blank' || target.hasAttribute('download')) return;

  const href = target.getAttribute('href');
  if (!href || href.startsWith('#')) return;

  const url = new URL(target.href, window.location.origin);

  if (url.origin !== window.location.origin) return;

  const modalId = getModalIdByPath(url.pathname);

  if (modalId) {
    e.preventDefault();

    // Extract query parameters from the clicked link
    const linkParams = {};
    url.searchParams.forEach((value, key) => {
      linkParams[key] = value;
    });

    await handleModalTriggerClick(modalId, linkParams);
  }
}
